import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Expander from '@amzn/meridian/expander';
import { useTranslation } from 'src/arb-wrapper';
import {
  type Title,
  type AssetDto,
} from 'src/store/generated/leela-api-generated';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Divider from '@amzn/meridian/divider';
import Table from '@amzn/meridian/table';
import TableRow from '@amzn/meridian/table/table-row';
import TableCell from '@amzn/meridian/table/table-cell';

import {
  ASSET_STATUSES,
  iconApproved,
  iconPending,
  iconRejected,
} from '../asset-status';
import { capitalizeFirstLetter } from 'src/utils';
import { assetStrategyKey } from 'src/utils/assetKeys';
import { resolveStrategyLabel } from 'src/utils/strategyLabels';

interface BatchedAssetListProps {
  batches: AssetDto[][];
  approvalStatus?: string;
  // productStrategyId → display name, shown in the Strategy column.
  strategyLabels?: Map<string, string>;
}

interface AssetListProps {
  assets: AssetDto[];
  approvalStatus?: string;
  index: number;
  strategyLabels?: Map<string, string>;
}

export const SingleAssetList = (props: { assets: AssetDto[]; strategyLabels?: Map<string, string> }) => {
  const { t } = useTranslation();

  const { assets, strategyLabels } = props;

  const TitleList = (props: { titleNames: Title[] }) => {
    return (
      <Column>
        {props.titleNames.map((title: Title) => {
          return (
            <Row key={title.name}>
              <Text type="b300">{title.name}</Text>
            </Row>
          );
        })}
      </Column>
    );
  };

  return (
    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
      <Table
        data-testid="single-asset-table-list"
        headerRows={1}
        showDividers={true}
        showStripes={false}
        spacing="medium"
      >
        <TableRow>
          <TableCell>{t('asset_detail_view_header_asset_id')}</TableCell>
          <TableCell>{t('asset_status_update_modal_titles')}</TableCell>
          <TableCell>{t('product_strategy_label')}</TableCell>
          <TableCell>{t('asset_detail_view_header_locale')}</TableCell>
          <TableCell>{t('asset_detail_view_header_asset_type')}</TableCell>
        </TableRow>
        {assets.map((asset: AssetDto) => {
          const rowKey = assetStrategyKey(asset);
          return (
          <TableRow
            data-testid={`single-asset-table-list-row-${asset.assetId}`}
            highlightOnHover={false}
            key={rowKey}
          >
            <TableCell>{asset.assetId}</TableCell>
            <TableCell>
              <TitleList titleNames={asset.titles} />
            </TableCell>
            <TableCell>
              {[resolveStrategyLabel(strategyLabels, asset.productStrategyId), '—'].find(Boolean)}
            </TableCell>
            <TableCell>{asset.locale}</TableCell>
            <TableCell>{asset.assetType}</TableCell>
          </TableRow>
          );
        })}
      </Table>
    </div>
  );
};

const BatchedAssetList: React.FC<BatchedAssetListProps> = (props) => {
  const { batches, approvalStatus, strategyLabels } = props;
  const { t } = useTranslation();

  return batches.map((assets, index) => {
    const title = `${t('asset_batch_of_total', {
      number: index + 1,
      total: batches.length,
    })} (${assets.length})`;

    return (
      <Column key={index}>
        <Text>{title}</Text>
        <SingleAssetListContainer
          approvalStatus={approvalStatus}
          assets={assets}
          index={index}
          strategyLabels={strategyLabels}
        />
      </Column>
    );
  });
};

export function approvalIcon (approvalStatus: string) {
  switch (approvalStatus) {
    case ASSET_STATUSES.REJECTED:
      return iconRejected;
    case ASSET_STATUSES.APPROVED:
      return iconApproved;
    case ASSET_STATUSES.PENDING:
      return iconPending;
    default:
      return null;
  }
}

const SingleAssetListContainer: React.FC<AssetListProps> = (props) => {
  const { assets, index, strategyLabels } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const title = <Text color="secondary">{t('click_for_more_details')}</Text>;

  return (
    <>
      <Box width="100%">
        {props.approvalStatus && (
          <Row alignmentHorizontal="justify">
            <Row>
              {approvalIcon(props.approvalStatus)}
              <Text type="b300">
                {t('modal_asset_approval_confirm_header', {
                  status: capitalizeFirstLetter(props.approvalStatus),
                })}
              </Text>
            </Row>
            <Text color="secondary">
              {assets.length} {t('tab_label_assets')}
            </Text>
          </Row>
        )}
        <Expander
          data-testid={`asset-batch-expander-${index}`}
          key={`asset-batch-${index}`}
          open={isExpanded}
          spacingInset="400"
          title={title}
          type="inline"
          onChange={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          <SingleAssetList assets={assets} strategyLabels={strategyLabels} />
        </Expander>
      </Box>
      <Divider />
    </>
  );
};

export const AssetList: React.FC<BatchedAssetListProps> = ({
  batches,
  approvalStatus,
  strategyLabels,
}) => (
  <Column spacingInset="none 300 none 300" width="100%">
    {batches.length === 1 && (
      <SingleAssetListContainer
        approvalStatus={approvalStatus}
        assets={batches[0]}
        index={0}
        strategyLabels={strategyLabels}
      />
    )}
    {batches.length > 1 && (
      <BatchedAssetList approvalStatus={approvalStatus} batches={batches} strategyLabels={strategyLabels} />
    )}
  </Column>
);
